import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ChevronRight, Eye } from "lucide-react";
import { toast } from "sonner";
import { Reveal, StatusBadge } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";

const WORKFLOW = ["Submitted", "Documentation Review", "Accepted", "Identity Capture", "Optional Services", "Certificate Review", "Verified", "Released"];
const VISIBILITY = ["private", "bb_only", "approved_buyers", "public", "on_hold", "sold", "unavailable"];
const VIS_LABEL = {
  private: "Private", bb_only: "Visible to BB only", approved_buyers: "Approved buyers",
  public: "Publicly presented", on_hold: "On hold", sold: "Sold", unavailable: "No longer available",
};

export default function DiamondDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/diamonds/${id}`); setD(data); }
    catch (err) { toast.error("Not found"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const advance = async (status) => {
    setBusy(true);
    try { const { data } = await api.patch(`/diamonds/${id}/status`, { status }); setD(data); toast.success(`Advanced to ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const setVisibility = async (visibility) => {
    try { await api.patch(`/diamonds/${id}/visibility`, { visibility }); setD((p) => ({ ...p, visibility })); toast.success("Visibility updated"); }
    catch { toast.error("Failed"); }
  };

  if (!d) return <div className="pt-40 text-center text-bb-silver/50">Loading…</div>;

  const stageIdx = WORKFLOW.indexOf(d.status);
  const nextStatus = WORKFLOW[stageIdx + 1];

  return (
    <div className="pt-28 pb-24 mx-auto max-w-[1200px] px-6 lg:px-10">
      <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-bb-silver/60 hover:text-bb-gold transition-colors duration-300">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.4} /> Back to dashboard
      </Link>

      <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-bb-silver/50">{d.reference}</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">{d.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <StatusBadge status={d.status} label={d.status} testid="detail-status" />
            {d.bb_number && <span className="font-mono text-sm text-bb-gold">{d.bb_number}</span>}
          </div>
        </div>
        {d.bb_number && (
          <a href={`${API}/certificate/${d.bb_number}/pdf`} target="_blank" rel="noreferrer" data-testid="detail-pdf-btn"
            className="btn-ghost rounded-full px-6 py-3 text-sm flex items-center gap-2 w-fit">
            <Download className="h-4 w-4" strokeWidth={1.3} /> Download certificate PDF
          </a>
        )}
      </div>

      {/* Workflow tracker */}
      <Reveal>
        <div className="glass rounded-2xl p-8 mt-8">
          <div className="label-eyebrow mb-6">Service workflow</div>
          <div className="flex flex-wrap gap-y-4">
            {WORKFLOW.map((step, i) => {
              const done = i <= stageIdx;
              return (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center min-w-[90px]">
                    <div className={`grid place-items-center h-9 w-9 rounded-full border text-xs transition-all duration-500 ${
                      done ? "border-bb-gold bg-bb-gold/15 text-bb-gold" : "border-white/12 text-bb-silver/40"
                    }`}>{i + 1}</div>
                    <span className={`mt-2 text-[0.6rem] text-center leading-tight ${done ? "text-bb-gold" : "text-bb-silver/45"}`}>{step}</span>
                  </div>
                  {i < WORKFLOW.length - 1 && <ChevronRight className={`h-4 w-4 mb-5 ${i < stageIdx ? "text-bb-gold" : "text-bb-silver/20"}`} strokeWidth={1.3} />}
                </div>
              );
            })}
          </div>

          {user?.role === "admin" && nextStatus && (
            <div className="mt-6 pt-6 border-t border-white/8 flex items-center gap-4">
              <span className="text-sm text-bb-silver/60">Administrator action:</span>
              <button data-testid="advance-status-btn" disabled={busy} onClick={() => advance(nextStatus)} className="btn-gold rounded-full px-6 py-2.5 text-sm">
                Advance to “{nextStatus}”
              </button>
              {nextStatus === "Verified" && <span className="text-xs text-bb-silver/45">Issues a BB number & live certificate</span>}
            </div>
          )}
          {user?.role !== "admin" && (
            <p className="mt-6 pt-6 border-t border-white/8 text-xs text-bb-silver/45">
              Status advances follow authorised Botswana Brilliance review. No certificate is issued automatically.
            </p>
          )}
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Reveal>
          <div className="glass rounded-2xl p-8">
            <div className="label-eyebrow mb-5">Diamond details</div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              {[["Carat", `${d.carat} ct`], ["Shape", d.shape], ["Colour", d.color], ["Clarity", d.clarity],
                ["Cut", d.cut], ["Origin", d.origin_region], ["Manufacturer", d.manufacturer || "Not provided"], ["Rough ref", d.rough_reference || "Not provided"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/6 py-2">
                  <span className="text-sm text-bb-silver/55">{k}</span>
                  <span className="text-sm text-bb-white">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {d.want_pride_mark && <Tag>Botswana Pride Mark</Tag>}
              {d.want_diamondeye && <Tag>DiamondEye product</Tag>}
              {d.want_lab_coordination && <Tag>Lab coordination · {d.lab_choice}</Tag>}
            </div>
            <div className="mt-5 text-sm text-bb-silver/60">Estimated fee: <span className="text-bb-gold">${d.estimated_fee_usd?.toLocaleString()} USD</span></div>
          </div>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="glass rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4"><Eye className="h-4 w-4 text-bb-gold" strokeWidth={1.2} /><span className="label-eyebrow">Market visibility</span></div>
              <select data-testid="visibility-select" value={d.visibility} onChange={(e) => setVisibility(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-[#0A1420] px-4 py-3 text-sm outline-none focus:border-bb-gold/50">
                {VISIBILITY.map((v) => <option key={v} value={v}>{VIS_LABEL[v]}</option>)}
              </select>
              <p className="text-xs text-bb-silver/45 mt-3">You retain ownership and control who can see this diamond.</p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="glass rounded-2xl p-8">
              <div className="label-eyebrow mb-5">History</div>
              <div className="space-y-0">
                {(d.history || []).map((h, i) => (
                  <div key={`${h.event}-${i}`} className="flex gap-4 py-2.5 border-b border-white/6 last:border-0">
                    <span className="text-xs text-bb-silver/45 font-mono whitespace-nowrap w-24">
                      {new Date(h.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}
                    </span>
                    <span className="text-sm text-bb-white flex-1">{h.event}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="rounded-full border border-bb-gold/25 bg-bb-gold/5 px-3.5 py-1.5 text-xs text-bb-gold">{children}</span>
);
